import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DataGridPremium, GridCellModes, useGridApiRef } from '@mui/x-data-grid-premium';
import { arrayOf, bool, func, shape, string } from 'prop-types';

import * as R from 'ramda';
import { createSnack } from 'snackbar/snackbarSlice';
import DialogExplicitConfirmation from 'common/dialog-explicit-confirmation/DialogExplicitConfirmation';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { GT_ZERO, INTEGER, REQUIRED } from 'utils/helpers/validationMessages';
import { isNumberWithOptionalFormatting } from 'utils/helpers/formValidations';
import { CircularProgress, Snackbar, Tooltip, Typography } from '@mui/material';
import { AuthContext } from 'authentication/AuthContext';
import StatusChip from './components/StatusChip';
import { distributionStatusTooltips, isEditableStatus } from './utils';

import {
  useDeleteDistributionMutation,
  useGetInventoryOwnersQuery,
  useLazyGetLocationInfoQuery,
} from './batchesEndpoints';

export const DISTRIBUTIONS_DRAWER_MODE = { EDIT: true, VIEW: false };

const AssociatedDistributionsGrid = ({
  associatedDistributions,
  setAssociatedDistributions,
  duplicateDistributionId,
  onEditPage,
  setIsDistributionsVolumeInvalid,
  setSelectedDistribution,
  setUpdatedRows,
  shipment,
  shipmentUpdates,
  refetchShipment,
}) => {
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();

  const [dialogType, setDialogType] = useState('');
  const [idToDelete, setIdToDelete] = useState(null);
  const [isExplicitDialogOpen, setIsExplicitDialogOpen] = useState(false);
  const [deleteDistribution] = useDeleteDistributionMutation();
  const [cellModesModel, setCellModesModel] = useState({});
  const dataCompanyId = R.path(['shipper', 'dataCompanyId'], shipment);
  const inventoryOwnersParams = { dataCompanyId };
  const [getLocationInfo, { isFetching: isFetchingLocationInfo }] = useLazyGetLocationInfoQuery();
  const { currentAffiliate } = useContext(AuthContext);
  const [fetchedLocations, setFetchedLocations] = useState(false);

  const {
    data: inventoryOwnersData = [],
    isFetching: isFetchingInventoryOwners,
    isSuccess: isSuccessInventoryOwners,
  } = useGetInventoryOwnersQuery({ params: inventoryOwnersParams });

  const bookingoriginId = R.path(['bookingOrigin', 'bookingOriginId'], shipmentUpdates);
  const productId = R.prop('productId', shipment);

  const isDistributionsVolumeInvalid = R.lt(
    R.max(
      parseInt(R.prop('volume', shipmentUpdates)?.replace(/,/g, '')),
      R.prop('volume', shipment)
    ),
    R.sum(R.pluck('volume', associatedDistributions))
  );

  useEffect(() => {
    setIsDistributionsVolumeInvalid(isDistributionsVolumeInvalid);
  }, [isDistributionsVolumeInvalid, setIsDistributionsVolumeInvalid]);

  const validateVolume = value =>
    R.cond([
      [R.isEmpty(R.__), R.always(REQUIRED)],
      [R.gt(1), R.always(GT_ZERO)],
      [val => !isNumberWithOptionalFormatting(val), R.always(INTEGER)],
      [R.T, R.always('')],
    ])(value);

  const [validationMessages, setValidationMessages] = useState({
    volume: '',
  });

  const handleDelete = (type, deleteId) => {
    setDialogType(type);
    setIdToDelete(deleteId);
    setIsExplicitDialogOpen(true);
  };

  const deleteSelectedDistribution = () => {
    deleteDistribution({ body: { distributionId: idToDelete } })
      .then(res => {
        setIsExplicitDialogOpen(false);
        if (res.error) {
          dispatch(
            createSnack({
              message: res.error.data.title,
              options: { variant: 'error' },
            })
          );
        } else {
          dispatch(
            createSnack({
              message: res.data.message,
            })
          );
        }
      })
      .finally(() => {
        refetchShipment();
        setTimeout(() => {
          setIdToDelete(null);
          setDialogType('');
        }, 500);
      });
  };

  const minWidth = 150;

  const handleUpdatedRowsChange = distribution => {
    setUpdatedRows(prev => {
      if (!prev.includes(distribution?.distributionId) && !distribution?.isNew)
        return [...prev, distribution?.distributionId];
      return prev;
    });
  };

  const updateRow = (distributionId, label, value) => {
    setAssociatedDistributions(
      R.map(
        R.ifElse(
          R.propEq('distributionId', distributionId),
          R.compose(R.tap(handleUpdatedRowsChange), R.assoc(label, value)),
          R.identity
        )
      )
    );

    apiRef.current.updateRows([{ distributionId: Number(distributionId), [label]: value }]);
  };

  const getLocation = locationName => {
    return R.find(
      location => location.locationName === locationName,
      fetchedLocations.length > 0 ? fetchedLocations : []
    );
  };

  const handleInventoryOwnerChange = (inventoryOwner, distributionId) => {
    updateRow(distributionId, 'inventoryOwner', inventoryOwner);
    updateRow(distributionId, 'inventoryOwnerName', R.prop('inventoryOwnerName', inventoryOwner));
  };

  const handleLocationChange = ({ locationName, id }, distributionId) => {
    updateRow(distributionId, 'locationId', id);
    updateRow(distributionId, 'locationName', locationName);
  };

  const handleVolumeChange = (distributionId, volume) => {
    const validationMessage = validateVolume(volume);
    setValidationMessages(R.mergeDeepLeft({ volume: { [distributionId]: validationMessage } }));
    if (R.isEmpty(validationMessage)) {
      updateRow(distributionId, 'volume', parseInt(volume));
      updateRow(distributionId, 'volumeDisplay', `${volume}`);

      const updatedRow = R.find(
        R.propEq('distributionId', distributionId),
        associatedDistributions
      );
      const location = getLocation(R.prop('locationName', updatedRow));
      if (location && !updatedRow.isNew) {
        updateRow(distributionId, 'locationId', location.id);
      }
    }
  };

  const getInventoryOwner = inventoryOwnerName => {
    return R.find(
      inventoryOwner => inventoryOwner.inventoryOwnerName === inventoryOwnerName,
      inventoryOwnersData
    );
  };

  const handleLocationCellClick = params => {
    const invOwnerData = getInventoryOwner(params.row.inventoryOwnerName);
    if (!params.isEditable) {
      const locationParams = {
        bookingOriginId: bookingoriginId,
        ...(params.row.isNew ? {} : { currentLocationId: params.row.locationId || null }),
        dataCompanyId,
        inventoryOwner: {
          companyCustomerIdName: invOwnerData?.inventoryOwnerIdName,
          dataCompanyId: currentAffiliate?.dataCompanyId,
        },
        productId,
        shipmentId: params.row.parentShipmentId || (shipment && shipment.shipmentId),
      };
      getLocationInfo(
        { params: locationParams },
        { skip: !R.has('bookingOrigin', shipmentUpdates) }
      ).then(res => {
        if (res.data) {
          setFetchedLocations(res.data);
        }
      });
    }
  };

  const columns = [
    {
      cellClassName: 'css-heap-redact',
      field: 'distributionId',
      headerName: 'Distribution Id',
      minWidth,
      renderCell: ({ row }) => (!R.prop('isNew', row) ? R.prop('distributionId', row) : ''),
    },
    {
      cellClassName: ({ row }) =>
        R.prop('isNew', row)
          ? 'css-heap-redact highlighter'
          : R.prop('distributionId', row) === duplicateDistributionId
          ? 'css-heap-redact error-row'
          : 'css-heap-redact',
      editable: onEditPage,
      field: 'inventoryOwnerName',
      getOptionLabel: option => R.defaultTo('', R.prop('inventoryOwnerName', option)),
      headerName: 'Inv. Owner',
      minWidth,
      renderCell: ({ row }) => R.prop('inventoryOwnerName', row),
      renderEditCell: ({ row }) => (
        <Autocomplete
          disableClearable
          disabled={!R.prop('isNew', row) || !isSuccessInventoryOwners || isFetchingInventoryOwners}
          getOptionLabel={option => R.defaultTo('', R.prop('inventoryOwnerName', option))}
          isOptionEqualToValue={R.eqProps('inventoryOwnerIdName')}
          loading={isFetchingInventoryOwners}
          onChange={(_, value) => handleInventoryOwnerChange(value, R.prop('distributionId', row))}
          options={inventoryOwnersData}
          renderInput={inputParams => (
            <Box sx={{ height: '100%' }}>
              <TextField
                {...inputParams}
                sx={{
                  '&.Mui-error': {
                    backgroundColor: 'error.light',
                    color: 'error.dark',
                  },
                  backgroundColor: 'secondary.light',
                }}
              />
            </Box>
          )}
          sx={{ '& fieldset': { border: 'none' }, backgroundColor: '#faadsd', width: '100%' }}
          value={getInventoryOwner(row.inventoryOwnerName)}
        />
      ),
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'status',
      headerName: 'Status',
      minWidth,
      renderCell: ({ row }) =>
        R.prop('status', row) && (
          <Tooltip placement="top-start" title={distributionStatusTooltips[R.prop('status', row)]}>
            <StatusChip status={R.prop('status', row)} />
          </Tooltip>
        ),
    },
    {
      cellClassName: ({ row }) =>
        R.prop('isNew', row)
          ? 'css-heap-redact highlighter'
          : R.prop('distributionId', row) === duplicateDistributionId
          ? 'css-heap-redact error-row'
          : R.prop('distributionId', row) && isEditableStatus(row.status)
          ? 'css-heap-redact editable-fields'
          : 'css-heap-redact',
      editable: onEditPage,
      field: 'locationName',
      getOptionLabel: option => R.defaultTo('', R.prop('locationName', option)),
      headerName: 'Location',
      minWidth,
      renderCell: ({ row }) => (
        <div
          onClick={event =>
            handleLocationCellClick({ field: 'locationName', id: row.distributionId, row }, event)
          }
        >
          {R.prop('locationName', row)}
        </div>
      ),

      renderEditCell: ({ row }) => (
        <Autocomplete
          disableClearable
          getOptionLabel={option => R.defaultTo('', R.prop('locationName', option))}
          isOptionEqualToValue={R.eqProps('id')}
          loading={isFetchingLocationInfo}
          loadingText="Loading..."
          onChange={(_, value) => handleLocationChange(value, R.prop('distributionId', row))}
          onOpen={() =>
            handleLocationCellClick({ field: 'locationName', id: row.distributionId, row })
          }
          options={fetchedLocations.length > 0 ? fetchedLocations : []}
          renderInput={inputParams => (
            <Box sx={{ height: '100%' }}>
              <TextField
                {...inputParams}
                slotProps={{
                  ...inputParams.slotProps,
                  input: {
                    ...inputParams.InputProps,
                    ...(inputParams.slotProps?.input ?? {}),
                    endAdornment: (
                      <>
                        {isFetchingLocationInfo ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {inputParams.InputProps?.endAdornment}
                      </>
                    ),
                  },
                }}
                sx={{
                  '&.Mui-error': {
                    backgroundColor: 'error.light',
                    color: 'error.dark',
                  },
                  backgroundColor: 'secondary.light',
                }}
              />
            </Box>
          )}
          sx={{ '& fieldset': { border: 'none' }, width: '100%' }}
          value={getLocation(row.locationName)}
        />
      ),
    },
    {
      cellClassName: ({ row }) =>
        R.prop('isNew', row)
          ? 'css-heap-redact highlighter'
          : R.prop('distributionId', row) && isEditableStatus(row.status)
          ? 'css-heap-redact editable-fields'
          : 'css-heap-redact',

      editable: onEditPage,
      field: 'volume',
      headerName: 'Volume (bbls)',
      minWidth,
      renderEditCell: ({ row }) => (
        <TextField
          error={R.not(R.isEmpty(validationMessages.volume[R.prop('distributionId', row)]))}
          helperText={validationMessages.volume[R.prop('distributionId', row)]}
          onChange={event => {
            handleVolumeChange(R.prop('distributionId', row), event.target.value);
          }}
          placeholder={`${R.prop('volume', row)}`}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">bbl</InputAdornment>,
            },
          }}
          sx={{
            '& fieldset': { border: 'none' },
            '& input': { p: '5px' },
            '&.Mui-error': {
              backgroundColor: 'error.light',
              color: 'error.dark',
            },
            backgroundColor: 'secondary.light',
            height: '100%',
            width: '100%',
          }}
        />
      ),
      type: 'number',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'chaining',
      headerName: 'Shipment Chain',
      minWidth,
      renderCell: ({ row }) => {
        const distributionId = R.prop('distributionId', row);
        if (!distributionId) return '';
        const shipmentChain = R.prop('shipmentChain', row) || 0;
        return `${shipmentChain} Recipient(s)`;
      },
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'reconsignmentControl',
      headerName: 'Recon. Control',
      minWidth,
      renderCell: ({ row }) =>
        R.prop('distributionId', row) && (R.prop('reconsignmentControl', row) ? 'On' : 'Off'),
    },
    {
      align: 'center',
      cellClassName: 'css-heap-redact',
      field: 'delete',
      headerAlign: 'center',
      headerName: 'Delete Distribution',
      minWidth,
      renderCell: ({ row }) =>
        R.prop('distributionId', row) && (
          <Button
            disabled={!onEditPage}
            onClick={() => {
              if (R.prop('isNew', row)) {
                setAssociatedDistributions(
                  R.reject(R.propEq('distributionId', R.prop('distributionId', row)))
                );
              } else {
                handleDelete('distribution', R.prop('distributionId', row));
              }
            }}
            variant="outlinedError"
          >
            Delete
          </Button>
        ),
    },
    {
      align: 'center',
      cellClassName: 'css-heap-redact',
      field: 'actions',
      headerAlign: 'center',
      headerName: 'Edit Distribution',
      minWidth,
      renderCell: ({ row }) =>
        R.prop('distributionId', row) && (
          <>
            <Button
              disabled={!onEditPage}
              onClick={() =>
                setSelectedDistribution({
                  distributionId: R.prop('distributionId', row),
                  mode: isEditableStatus(row.status)
                    ? DISTRIBUTIONS_DRAWER_MODE.EDIT
                    : DISTRIBUTIONS_DRAWER_MODE.VIEW,
                })
              }
              variant="outlined"
            >
              {isEditableStatus(row.status) ? 'Edit' : 'View'}
            </Button>
          </>
        ),
    },
  ];

  const renderConfirmDialog = () => {
    return (
      <DialogExplicitConfirmation
        confirmationMessage={`If you still want to delete the ${dialogType}, enter the word below and click Confirm.`}
        confirmationValue="DELETE"
        dialogOpen={isExplicitDialogOpen}
        handleCloseDialog={() => {
          setIsExplicitDialogOpen(false);
          setTimeout(() => {
            setDialogType('');
            setIdToDelete(null);
          }, 600);
        }}
        label='Type "DELETE"'
        onConfirm={deleteSelectedDistribution}
        title={`Are you sure you want to delete this ${dialogType}?`}
        warningList={['This action cannot be undone.']}
      />
    );
  };

  const handleCellClick = useCallback((params, event) => {
    if (!params.isEditable) {
      return;
    }

    // Ignore portal
    if (event.target.nodeType === 1 && !event.currentTarget.contains(event.target)) {
      return;
    }

    setCellModesModel(prevModel => {
      return {
        // Revert the mode of the other cells from other rows
        ...Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {}
            ),
          }),
          {}
        ),
        [params.id]: {
          // Revert the mode of other cells in the same row
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {}
          ),
          [params.field]: { mode: GridCellModes.Edit },
        },
      };
    });
  }, []);

  const handleCellModesModelChange = useCallback(newModel => {
    setCellModesModel(newModel);
  }, []);

  const processRowUpdate = (currentRow, updatedRow) => {
    return updatedRow;
  };

  return (
    <>
      <Box sx={{ display: 'block', mb: 9, overflow: 'visible', position: 'relative' }}>
        {renderConfirmDialog()}
        <DataGridPremium
          apiRef={apiRef}
          autosizeOnMount
          autosizeOptions={{
            expand: true,
            includeHeaders: true,
            includeOutliers: false,
          }}
          cellModesModel={cellModesModel}
          cellSelection
          columns={columns}
          getRowId={R.prop('distributionId')}
          hideFooter
          id="distributionId"
          initialState={{
            aggregation: {
              model: {
                volume: 'sum',
              },
            },
          }}
          isCellEditable={R.T}
          onCancel
          onCellClick={handleCellClick}
          onCellModesModelChange={handleCellModesModelChange}
          pagination
          processRowUpdate={processRowUpdate}
          rows={associatedDistributions}
        />
      </Box>
      <Snackbar open={isDistributionsVolumeInvalid}>
        <Box
          sx={{
            backgroundColor: 'error.dark',
            borderRadius: 0.5,
            height: '150px',
            p: 2.6,
            width: '270px',
          }}
        >
          <Typography sx={{ color: 'white' }} variant="body1">
            The shipment volume is less than the total sum of associated distributions. Please
            modify before saving your shipment request
          </Typography>
        </Box>
      </Snackbar>
    </>
  );
};

AssociatedDistributionsGrid.propTypes = {
  associatedDistributions: arrayOf(shape({})).isRequired,
  duplicateDistributionId: string.isRequired,
  onEditPage: bool.isRequired,
  refetchShipment: func.isRequired,
  setAssociatedDistributions: func.isRequired,
  setIsDistributionsVolumeInvalid: func.isRequired,
  setSelectedDistribution: func.isRequired,
  setUpdatedRows: func.isRequired,
  shipment: shape({}).isRequired,
  shipmentUpdates: shape({}).isRequired,
};

export default AssociatedDistributionsGrid;
